import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react"
import { isEmpty } from "lodash"
import { IntradayBatch, useIntradayBatches, useLastIntradayBatch } from "hooks/intraday"
import { DataStatus, OK } from "helpers/data-statuses"
import { getToday, IsoDate } from "helpers/types/dates"
import { isPinnedHistoricalBatch } from "gatherers/helpers"
import { getIntradayBatchMoment } from "helpers/intraday"
import { IntradayBatchMetadata } from "model/state"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "../context/dashboard-id"
import { DEFAULT_CORE_STATE } from "../store/core-slice"

/**
 * Helper function to calculate intraday state
 * @param isIntraday - Base intraday flag
 * @param endDate - Current end date
 * @param intradayDate - Today's date (intraday date)
 * @param lastIntradayBatch - Intraday batch data
 * @param intradayBatchStatus - Status of intraday batch
 */
export function calculateIntradayState(
  isIntraday: boolean,
  endDate: IsoDate,
  intradayDate: IsoDate,
  lastIntradayBatch: IntradayBatchMetadata | null,
  intradayBatchStatus: DataStatus,
): [{
  isIntradayMode: boolean
  intradayDataAvailable: boolean
  isEndDateIntraday: boolean
  lastIntradayBatchId: string | null
}, DataStatus] {
  const intradayDataAvailable = !isEmpty(lastIntradayBatch) && intradayBatchStatus === OK
  const isEndDateIntraday = endDate === intradayDate
  const isIntradayMode = isIntraday && isEndDateIntraday

  return [
    {
      isIntradayMode,
      intradayDataAvailable,
      isEndDateIntraday,
      lastIntradayBatchId: lastIntradayBatch?.id ?? null,
    },
    intradayBatchStatus,
  ]
}

export function useIsIntradayMode(): boolean {
  const dashboardId = useDashboardId()
  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  return coreState.isIntraday && coreState.endDate === getToday()
}

export function useIntradayState() {
  const dashboardId = useDashboardId()
  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const { isIntraday, endDate, instrument, instrumentsMetadata } = coreState
  const [lastIntradayBatch, intradayBatchStatus] = useLastIntradayBatch(instrument, instrumentsMetadata?.original?.intradayEnabled)
  const today = getToday()

  // useLastIntradayBatch blips null for one render per live-refresh tick; without latching,
  // isIntradaySimulationEligible would misread that as a stale batchId. Read via
  // useSyncExternalStore, not ref.current, since refs are write-only during render.
  const lastKnownIntradayBatchRef = useRef<IntradayBatchMetadata | null>(null)
  const getLastKnownIntradayBatch = useCallback(() => lastKnownIntradayBatchRef.current, [])
  const subscribeToLastKnownIntradayBatch = useCallback(() => () => undefined, [])
  const latchedLastIntradayBatch = useSyncExternalStore(subscribeToLastKnownIntradayBatch, getLastKnownIntradayBatch)

  useEffect(() => {
    if (intradayBatchStatus === OK) {
      lastKnownIntradayBatchRef.current = lastIntradayBatch
    }
  }, [intradayBatchStatus, lastIntradayBatch])

  const stableLastIntradayBatch = intradayBatchStatus === OK ? lastIntradayBatch : latchedLastIntradayBatch

  return useMemo(() => {
    return calculateIntradayState(isIntraday, endDate, today, stableLastIntradayBatch, intradayBatchStatus)
  }, [isIntraday, endDate, today, stableLastIntradayBatch, intradayBatchStatus])
}

// No product-specified cap on batch cadence exists yet (an open question in the intraday-batch
// spec) — a sub-minute-cadence trading day could otherwise fire an unbounded number of queries
// (one per batch). Keep the most recent N until product settles on a real number.
export const MAX_INTRADAY_BATCH_TREND_POINTS = 20

const EMPTY_BATCHES: IntradayBatch[] = []

// Unifies tooltip-header mode only — isOneDayMode is still derived separately for click/pan gating.
export type IntradayChartMode = "none" | "liveTick" | "batchTrend"

export function getIntradayChartMode(isBatchTrendMode: boolean, isLiveTickEligible: boolean): IntradayChartMode {
  if (isBatchTrendMode) return "batchTrend"
  if (isLiveTickEligible) return "liveTick"
  return "none"
}

/**
 * Shared by every multi-date graph template (risk/exposure/factor-sensitivity/content-set): once
 * a portfolio has any committed intraday batch today, its points are appended to the live-tick
 * series rather than replacing it. Holds off returning any batches until the batch list itself
 * resolves with at least one completed batch — lastIntradayBatchId isn't date-scoped (it's just
 * "the last completed batch", which could be from a prior day), so a resolved-but-empty list for
 * today must NOT be treated as "yes, batch trend mode" or the graph gets stuck on "not available
 * yet" instead of falling back to the live-tick/day-based path.
 */
export function useIntradayBatchTrendMode(
  startDate: IsoDate,
  endDate: IsoDate,
  isIntradayMode: boolean,
  lastIntradayBatchId: string | null,
  intradayBatchId: string | null | undefined,
) {
  const dashboardId = useDashboardId()
  const coreState = useAppSelector((s) => s.dashboard.core[dashboardId] ?? DEFAULT_CORE_STATE)
  const { instrument, instrumentsMetadata } = coreState
  const today = getToday()

  // A pinned historical batch (open trades or an active optimization comparison blocking
  // auto-refresh) must keep its frozen single-batch snapshot — batch-trend mode would otherwise
  // silently replace it with the full-day series, including batches after the pinned one.
  const isPotentialBatchTrendMode = isIntradayMode && startDate === endDate && lastIntradayBatchId != null
    && !isPinnedHistoricalBatch(intradayBatchId, lastIntradayBatchId)
  // Keyed on lastIntradayBatchId rather than the live-tick asOf (see hooks/intraday.ts), so this
  // no longer blips to a non-OK status on every socket pulse — no latch needed for that blip.
  const [intradayBatches, intradayBatchesStatus] = useIntradayBatches(
    instrument,
    instrumentsMetadata?.original?.intradayEnabled,
    isPotentialBatchTrendMode ? today : undefined,
    lastIntradayBatchId,
  )

  const { completedBatches, isTruncated } = useMemo(() => {
    if (intradayBatchesStatus !== OK) {
      return { completedBatches: EMPTY_BATCHES, isTruncated: false }
    }
    const completed = (intradayBatches ?? []).filter(batch => batch.complete)
    // Sort always, even under the cap — an unsorted, API-order-dependent array thrashes stableCacheKey.
    const sorted = [...completed].sort((a, b) => getIntradayBatchMoment(a).valueOf() - getIntradayBatchMoment(b).valueOf())
    const truncated = sorted.length > MAX_INTRADAY_BATCH_TREND_POINTS
    return {
      completedBatches: truncated ? sorted.slice(-MAX_INTRADAY_BATCH_TREND_POINTS) : sorted,
      isTruncated: truncated,
    }
  }, [intradayBatchesStatus, intradayBatches])

  const isBatchTrendMode = isPotentialBatchTrendMode && completedBatches.length > 0

  return { isBatchTrendMode, completedBatches, isTruncated }
}
